// useRecordEditor — S1: jednotný editačný lifecycle pre celý Matrix v2
// ════════════════════════════════════════════════════════════════════
// Ústava: 53.7 (LOCK multi-user) · 53.1 (DIRTY dialóg) · 12.0 bod 3
//         (Form→Confirm) · 53.8 (DIFF pred uložením) · 12.0 bod 1 (audit).
//
// PREČO: každý editačný modal v Matrixe robil lock/dirty/confirm zvlášť —
// niekde chýbal lock, niekde audit. Tento hook ich spája do JEDNÉHO miesta,
// takže sa nedajú vynechať (ústava 9.D — anti-chyba systém).
// KAŽDÝ edit/new modal MUSÍ ísť cez useRecordEditor.
//
// POUŽITIE:
//   const EDIT_FIELDS = [                       // modul-level const (stabilné!)
//     { name:'predak',  label:'Predák' },
//     { name:'zakazka', label:'Zákazka', format: v => v || '—' },
//   ]
//   const ed = useRecordEditor({
//     resource: 'work_schedule',                // lock resource + audit module
//     id: record?.id || null,                   // null = NOVÝ záznam (bez locku)
//     user,
//     initial: { predak:'', zakazka:'' },
//     fields: EDIT_FIELDS,
//     onSubmit: async (values) => { await fetchAuth(...) },
//   })
//
//   ed.phase        'form' | 'confirm'
//   ed.values       aktuálne hodnoty formulára
//   ed.set(n,v)     zmeň jedno pole · ed.setValues({...}) viac polí naraz
//   ed.isDirty      zmenil sa formulár oproti initial?
//   ed.canEdit      true ak lock získaný (alebo nový záznam) — inak read-only
//   ed.lockedBy     meno usera čo drží lock (alebo null)
//   ed.diff         [{label, from, to}] — LEN zmenené polia (pre Confirm DIFF)
//   ed.goConfirm(validate?)  form → confirm; validate(values) vráti chybu|null
//   ed.back()       confirm → form
//   ed.submit()     spusti onSubmit; po úspechu uvoľní lock; vráti bool
//   ed.submitting · ed.error · ed.setError
//   ed.requestClose(closeFn)  zavri — ak dirty → DIRTY dialóg, inak rovno
//   ed.dirtyProps   rozbaľ do <Modal.DirtyConfirm {...ed.dirtyProps} />

import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useLock } from './useLock.js'
import { useDirtyState } from './useDirtyState.js'
import { setDirty } from './unsavedGuard.js'

export function useRecordEditor({
  resource,
  id = null,
  user,
  initial = {},
  fields = [],
  onSubmit,
  keepLockAfterSave = false,   // true = po uložení NEuvoľní zámok → ostane editovateľné (priebežné ukladanie, napr. Kalkulácie)
}) {
  const isNew = !id
  const initialRef = useRef(initial)        // snapshot pre DIFF
  const closeFnRef = useRef(null)
  const dirty = useDirtyState(initial)
  // Lock len pre existujúci záznam — nový záznam ešte nemá čo zamknúť (53.7)
  const lock = useLock(isNew ? null : resource, isNew ? null : id, user)

  const [phase, setPhase] = useState('form')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [dirtyDialog, setDirtyDialog] = useState(false)

  const values = dirty.value
  const isDirty = dirty.isDirty
  const canEdit = isNew || lock.acquired
  const lockedBy = lock.lockInfo ? lock.lockInfo.by : null

  const set = useCallback((name, v) => {
    dirty.setValue(prev => ({ ...prev, [name]: v }))   // p97: funkčný zápis — viac polí za sebou (napr. blok kontaktnej osoby) sa už neprepíše
    setError(null)
  }, [dirty])

  const setValues = useCallback((obj) => {
    dirty.setValue({ ...dirty.value, ...obj })
    setError(null)
  }, [dirty])

  // DIFF — len zmenené polia (ústava 53.8) — vstup pre Confirm fázu
  const diff = useMemo(() => {
    // DIFF_ARRAYS_v1 — JSON.stringify pre arrays/objekty (predtým String(arr) = "[object Object]")
    const init = initialRef.current || {}
    const norm = (x) => {
      if (x === null || x === undefined) return ''
      if (typeof x === 'object') return JSON.stringify(x)
      return String(x)
    }
    return fields.map(f => {
      const rawFrom = init[f.name]
      const rawTo = values ? values[f.name] : undefined
      if (norm(rawFrom) === norm(rawTo)) return null
      const defaultFmt = (x) => {
        if (x === null || x === undefined || x === '') return '—'
        if (Array.isArray(x)) return `${x.length}× ${f.label || 'položiek'}`
        if (typeof x === 'object') return JSON.stringify(x).slice(0, 60)
        return String(x)
      }
      const fmt = f.format || defaultFmt
      return { name: f.name, label: f.label, from: fmt(rawFrom), to: fmt(rawTo) }
    }).filter(Boolean)
  }, [fields, values])

  // form → confirm. validate(values) → string (chyba) | null/false (OK)
  const goConfirm = useCallback((validate) => {
    setError(null)
    if (typeof validate === 'function') {
      const msg = validate(values)
      if (msg) { setError(msg); return false }
    }
    if (!isNew && diff.length === 0) {
      setError('Žiadne zmeny — nie je čo uložiť.')
      return false
    }
    setPhase('confirm')
    return true
  }, [values, diff, isNew])

  const back = useCallback(() => { setPhase('form'); setError(null) }, [])

  const submit = useCallback(async () => {
    if (submitting) return false
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(values, { isNew, diff })
      dirty.reset(values)               // už nie je dirty
      if (keepLockAfterSave) {
        initialRef.current = values     // priebežné ukladanie — nový DIFF baseline, zámok drž → ostane editovateľné
      } else if (!isNew) {
        lock.release()                  // štandard 53.7 — po uložení uvoľni lock
      }
      return true
    } catch (e) {
      setError(e?.message || 'Uloženie zlyhalo.')
      setPhase('form')
      return false
    } finally {
      setSubmitting(false)
    }
  }, [submitting, values, onSubmit, isNew, diff, dirty, lock, keepLockAfterSave])

  // Globálny navigačný guard (ústava 53.1) — kým je dirty, varuj pri odchode (klik do menu / iná zákazka / tab / refresh).
  // Sidebar a navigačné body volajú confirmLeave(); „Uložiť" v dialógu spustí submit. Odhlási sa pri zmiznutí (unmount).
  useEffect(() => {
    const key = 'rec:' + (resource || 'modul') + ':' + (id || 'new')
    setDirty(key, isDirty, async () => { const ok = await submit(); if (!ok) throw new Error('Uloženie zlyhalo') })
    return () => setDirty(key, false)
  }, [isDirty, resource, id, submit])

  // requestClose — ak dirty → DIRTY dialóg (53.1), inak rovno zavri + uvoľni lock
  const requestClose = useCallback((closeFn) => {
    closeFnRef.current = closeFn || null
    if (dirty.isDirty) {
      setDirtyDialog(true)
    } else {
      if (!isNew) lock.release()
      if (closeFnRef.current) closeFnRef.current()
    }
  }, [dirty.isDirty, isNew, lock])

  // Props priamo do <Modal.DirtyConfirm /> (53.1 — Uložiť / Pokračovať / Zahodiť)
  const dirtyProps = {
    open: dirtyDialog,
    onSave: async () => {
      const ok = await submit()
      setDirtyDialog(false)
      if (ok && closeFnRef.current) closeFnRef.current()
    },
    onContinue: () => setDirtyDialog(false),
    onDiscard: () => {
      dirty.discard()
      if (!isNew) lock.release()
      setDirtyDialog(false)
      if (closeFnRef.current) closeFnRef.current()
    },
  }

  return {
    isNew, phase, values, set, setValues, isDirty,
    canEdit, lockedBy, lock,
    diff, goConfirm, back, submit, submitting, error, setError,
    requestClose, dirtyProps,
  }
}

export default useRecordEditor
